import { assign, createMachine, fromCallback } from 'xstate';
import { ls } from '~/utils/utils';

type Context = {
  key: string;
  prevWidth: number;
  width: number;
  positionX: number;
  defaultWidth: number;
};

type Events =
  | React.PointerEvent<HTMLElement>
  | React.MouseEvent<HTMLElement>
  | { type: 'cancel' }
  | { type: 'keydown' };

type Input = {
  defaultWidth?: number;
  key: string;
};

const isPointerEvent = (event: Events): event is React.PointerEvent<HTMLElement> & { target: HTMLElement } =>
  event.type.startsWith('pointer') && 'target' in event;

const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;

/** @xstate-layout N4IgpgJg5mDOIC5QCc4EsBeYB0aIBswBiABwHs0A7AFzGQjIHdKBtABgF1FRzY1q0ZStxAAPRAFoAzAA4AnNgCMAFjYA2AEwBWADQgAnolnYZWuVIDsWgL7W9qPllwFiEAEb4AxvjSeA1uxcSCC8-ILCweIIEopsymrYbBpSypa6BogyithqUlpqSWoWphYqyrb26E4QyACGUFBUUKQUNHQAtmQAbmCBIqECQiJRslJKMlZ6hghSChZ5RRZsMlKrqyoVIA6YODX1jZTN5FS0yACuJH3BA+HDksqKCRYaavnaU4gPCWxapuZWm221TqDSaLROdE8tUonjA+CuPDIfEGEVAUQkX2wz1emnS0zkCSKbCs8g0JTKgKquxBByOrVOZDO1ARISRYSGkXujyxLzeeMQGmJ2DkxK0WkFKzWUkUlMc1P2YKhMLhLJuHLRkikRWwv0F0veGRmii0Jjkyi0+psm0oZAgcBEQLA-TZKLu0ViZsSyVSk0NGJkOVUuNlO2chGdyNunOi8hNDzYxtxHwQEsDKWNimKWlKynKdi2VOwe1Bhwj7NRYgFimys3NluTmZNMg0Zo0S0laxltmsQA */
const resizeMachine = createMachine(
  {
    id: 'resize',
    initial: 'idle',
    types: {} as {
      context: Context;
      events: Events;
      input: Input;
    },
    context: ({ input }) => ({
      prevWidth: 0,
      key: input.key ?? 'resizeMachine.width',
      width: Number(ls.get<string>(input.key)) || 0,
      defaultWidth: input.defaultWidth ?? 0,
      positionX: 0,
    }),
    states: {
      idle: {
        entry: [
          ({ context }) => {
            ls.set(context.key, String(context.width));
          },
          assign({
            positionX: ({ context }) => context.defaultWidth + context.width,
          }),
        ],
        on: {
          pointerdown: {
            target: 'dragging',
          },
          dblclick: {
            target: 'idle',
            actions: [
              ({ context }) => {
                ls.set(context.key, String(0));
              },
              assign({
                width: 0,
                positionX: ({ context }) => context.defaultWidth,
              }),
            ],
          },
        },
      },
      dragging: {
        entry: [
          'setPointerCapture',
          assign({
            prevWidth: ({ context }) => context.width,
          }),
        ],
        invoke: {
          src: fromCallback(({ sendBack }) => {
            const handler = (e: KeyboardEvent) => {
              if (e.key === 'Escape') {
                sendBack({ type: 'cancel' });
              }
            };
            window.addEventListener('keydown', handler);
            return () => window.removeEventListener('keydown', handler);
          }),
        },
        on: {
          pointermove: {
            actions: ['updateWidth'],
          },
          pointerup: {
            target: 'idle',
            actions: ['releasePointerCapture'],
          },
          pointercancel: {
            target: 'idle',
            actions: ['releasePointerCapture'],
          },
          pointerout: {
            target: 'idle',
            actions: ['releasePointerCapture'],
          },
          cancel: {
            target: 'idle',
            actions: [
              assign({
                width: ({ context }) => context.prevWidth,
              }),
            ],
          },
        },
      },
    },
  },
  {
    actions: {
      updateWidth: assign({
        width: ({ context, event }) => {
          if (!isPointerEvent(event)) return context.width;
          const rect = event.target.getBoundingClientRect();
          const { left, width } = rect;

          const desiredWidth =
            event.clientX - left + context.prevWidth - (context.positionX === context.defaultWidth ? 0 : width / 2);
          const innerWidth = window.innerWidth - getScrollbarWidth();
          const calculatedWidth =
            desiredWidth + context.defaultWidth > innerWidth
              ? innerWidth - context.defaultWidth + width / 2
              : desiredWidth;

          if (calculatedWidth + context.defaultWidth < 0) return -context.defaultWidth;
          return calculatedWidth;
        },
      }),
      releasePointerCapture: ({ event }) => {
        if (!isPointerEvent(event)) return;
        event.target.releasePointerCapture(event.pointerId);
      },
      setPointerCapture: ({ event }) => {
        if (!isPointerEvent(event)) return;
        event.target.setPointerCapture(event.pointerId);
      },
    },
  }
);

export default resizeMachine;
